import {StandardDNACodon} from 'biojs/Codons';

export enum GCLevel {
  ANY = 'ANY',
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CUSTOM = 'CUSTOM',
}

export const GC_LEVEL_NAME: {[key in GCLevel]: string} = {
  ANY: 'Any (0 to 1)',
  LOW: 'Low (below 0.33)',
  MEDIUM: 'Medium (0.33 to 0.66)',
  HIGH: 'High (above 0.66)',
  CUSTOM: 'Custom range',
};

export interface GCContent {
  min: number | null;
  max: number | null;
}

export const DEFAULT_STEM = 20;
export const DEFAULT_WINDOW = 200;

export type TierOneOrganism =
  | 'A.thaliana'
  | 'Brassica.napus'
  | 'D.melanogaster'
  | 'Dog'
  | 'E.coli'
  | 'Hamster'
  | 'Human'
  | 'Mouse'
  | 'Pichia.pastoris'
  | 'Rattus.norvegicus'
  | 'S.frugiperda'
  | 'Schizosaccharomyces.pombe'
  | 'Xenopus.laevis'
  | 'Yeast'
  | 'Zea.mays';

export type TierTwoOrganism =
  | 'Bacillus.subtilis'
  | 'Coccidioides.immitis'
  | 'Desulfovibrio.vulgaris (Hildenborough)'
  | 'Giardia.lamblia (ATCC 50803)'
  | 'Lactococcus.lactis'
  | 'Leishmania.donovani'
  | 'Rhodobacter.sphaeroides (2.4.1)'
  | 'Phaeodactylum.tricornutum'
  | 'Staphylococcus.epidermidis (ATCC 12228)'
  | 'Toxoplasma.gondii'
  | 'Trypanosoma.cruzi';

export type Organism = TierOneOrganism | TierTwoOrganism;

export const CodonOptimizationPreferences = {
  MODAL_ORGANISM: 'codonModalOrganismPref',
  AVOID_HAIRPINS: 'codonOptimizationAvoidHairpinsPref',
  CUTSITES: 'codonOptimizationCutsitesPref',
  GC_LEVEL: 'codonOptimizationGCLevelPref',
  HAIRPIN_STEM: 'codonOptimizationHairpinStemPref',
  HAIRPIN_WINDOW: 'codonOptimizationHairpinWindowPref',
  ORGANISM: 'codonOptimizationOrganismPref',
  URIDINE: 'codonOptimizationUridinePref',
  REDUCE_PATTERNS: 'codonOptimizationReducePatternsPref',
  METHOD: 'codonOptimizationMethodPref',
  GC_CONTENT: 'codonOptimizationGCContentPref',
};

interface CodonChange {
  location: number;
  codons: {
    original: StandardDNACodon;
    optimized: StandardDNACodon;
  };
  codonFreqs: {
    original: number;
    optimized: number;
  } | null;
}

export interface OptimizeCodonsResult {
  optimizedBases: string;
  optimizationMetrics: OptimizationMetrics;
  changes: Array<CodonChange>;
}

export interface PatternToReduceMetric {
  pattern: string;
  original: number;
  optimized: number;
}

export interface OptimizationMetrics {
  numCodonsChanged: number;
  numRareCodons: {
    original: number;
    optimized: number;
  } | null;
  gcContent: {
    original: number;
    optimized: number;
  };
  thymineContent: {
    original: number;
    optimized: number;
  };
  numHairpins: {
    original: number;
    optimized: number;
  };
  patterns: Array<PatternToReduceMetric>;
}

export interface HairpinParameter {
  value: number;
  error: string;
}

export enum CodonOptimizationSelectionTab {
  AVOIDED_CUTSITES = 'AVOIDED_CUTSITES',
  PRESERVED_CUTSITES = 'PRESERVED_CUTSITES',
  PROTECTED_REGIONS = 'PROTECTED_REGIONS',
  REDUCED_PATTERNS = 'REDUCED_PATTERNS',
}

// Keep in sync with value in optimize_codons in backend
export const MAXIMUM_PATTERN_LENGTH = 10;
export const NXKMER_PATTERN_RE = /^(\d+)x(\d+)mer$/;
export const MAXIMUM_REPEAT_PATTERN_LENGTH = 50; // Size of n times k in a nxkmer pattern
export enum CodonOptimizationMethod {
  'MATCH_CODON_USAGE' = 'MATCH_CODON_USAGE',
  'USE_BEST_CODON' = 'USE_BEST_CODON',
}
